import React from "react";
import { Link } from "react-router-dom";

const TermsPage = () => {
  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <Link to="/" style={styles.logo}>striply</Link>
        <nav style={styles.nav}>
          <Link to="/editor" style={styles.navLink}>Editor</Link>
          <Link to="/about" style={styles.navLink}>About</Link>
        </nav>
      </header>

      <main style={styles.main}>
        <h1 style={styles.title}>Terms of Service</h1>
        <p style={styles.updated}>Last updated: February 2025</p>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Acceptance of Terms</h2>
          <p style={styles.text}>
            By accessing and using Striply, you agree to be bound by these Terms
            of Service. If you do not agree to these terms, please do not use the
            service.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Description of Service</h2>
          <p style={styles.text}>
            Striply is a web-based comic layout design tool that allows users to
            create, customize, and export comic page layouts. The service includes
            features for panel arrangement, speech bubble creation, and PDF export.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>3. User Content</h2>
          <p style={styles.text}>
            You retain ownership of any content you create or upload using Striply,
            including layouts, images, and text. You are responsible for ensuring
            you have the rights to use any images or content you upload.
          </p>
          <p style={styles.text}>
            You agree not to upload content that:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Infringes on intellectual property rights</li>
            <li style={styles.listItem}>Contains illegal or harmful material</li>
            <li style={styles.listItem}>Violates any applicable laws</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Acceptable Use</h2>
          <p style={styles.text}>
            You agree to use Striply only for lawful purposes and in accordance
            with these terms. You agree not to:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              Attempt to interfere with the proper functioning of the service
            </li>
            <li style={styles.listItem}>
              Use automated systems to access the service in a manner that could
              overload our systems
            </li>
            <li style={styles.listItem}>
              Attempt to access areas of the service not intended for public access
            </li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Service Availability</h2>
          <p style={styles.text}>
            We strive to keep Striply available and functional, but we do not
            guarantee uninterrupted access. The service may be temporarily
            unavailable for maintenance, updates, or due to circumstances beyond
            our control.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Limitation of Liability</h2>
          <p style={styles.text}>
            Striply is provided "as is" without warranties of any kind. We are not
            liable for any damages arising from your use of the service, including
            but not limited to data loss, business interruption, or any indirect
            damages.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Intellectual Property</h2>
          <p style={styles.text}>
            The Striply name, logo, and website design are our intellectual property.
            You may not use these without permission. The underlying technology and
            code remain our property.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Advertising</h2>
          <p style={styles.text}>
            Striply may display advertisements to support the free tier of the
            service. By using the service, you consent to the display of such
            advertisements.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Changes to Terms</h2>
          <p style={styles.text}>
            We may modify these terms at any time. Continued use of the service
            after changes constitutes acceptance of the new terms. We will indicate
            the date of the most recent revision.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>10. Termination</h2>
          <p style={styles.text}>
            We reserve the right to terminate or suspend access to the service
            for violations of these terms or for any other reason at our discretion.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>11. Contact</h2>
          <p style={styles.text}>
            If you have questions about these terms, please contact us
            at <a href="mailto:contact@striply.xyz" style={styles.link}>contact@striply.xyz</a>.
          </p>
        </section>
      </main>

      <footer style={styles.footer}>
        <nav style={styles.footerNav}>
          <Link to="/" style={styles.footerLink}>Home</Link>
          <Link to="/editor" style={styles.footerLink}>Editor</Link>
          <Link to="/about" style={styles.footerLink}>About</Link>
          <Link to="/privacy" style={styles.footerLink}>Privacy</Link>
        </nav>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#1a1a1a",
    lineHeight: 1.7,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#f5f0e8",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 40px",
    borderBottom: "2px solid #1a1a1a",
  },
  logo: {
    fontSize: 28,
    fontWeight: 700,
    color: "#1a1a1a",
    textDecoration: "none",
    fontFamily: "'Georgia', serif",
  },
  nav: {
    display: "flex",
    gap: 20,
  },
  navLink: {
    color: "#555",
    textDecoration: "none",
    fontSize: 15,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  main: {
    flex: 1,
    maxWidth: 720,
    margin: "0 auto",
    padding: "48px 24px",
  },
  title: {
    fontSize: 42,
    fontWeight: 700,
    marginBottom: 8,
    color: "#1a1a1a",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  updated: {
    fontSize: 14,
    color: "#888",
    marginBottom: 40,
    fontStyle: "italic",
  },
  section: {
    marginBottom: 28,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 10,
    color: "#1a1a1a",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  text: {
    fontSize: 15,
    marginBottom: 12,
    color: "#444",
  },
  list: {
    paddingLeft: 24,
    marginBottom: 12,
  },
  listItem: {
    fontSize: 15,
    marginBottom: 8,
    color: "#444",
  },
  link: {
    color: "#1a73e8",
    textDecoration: "underline",
  },
  footer: {
    padding: "24px 40px",
    borderTop: "2px solid #1a1a1a",
    background: "#f5f0e8",
  },
  footerNav: {
    display: "flex",
    justifyContent: "center",
    gap: 32,
  },
  footerLink: {
    color: "#666",
    textDecoration: "none",
    fontSize: 14,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
};

export default TermsPage;
